import styled from "styled-components";
import { mobileAuthSideContainer, mobileAuthMainContainer } from "../../../../styles/authShellMixins";

// ── Layout ─────────────────────────────────────────────────────────────────────

export const MainContainer = styled.div `
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  background: var(--app-bg-gradient);
  background-size: 400% 400%;
  font-family: "Inter", "Helvetica Neue", sans-serif;
  overflow-x: hidden;

  ${mobileAuthMainContainer}
`;

export const SideContainer = styled.div `
  scrollbar-width: none;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  height: 100vh;
  min-height: 100vh;
  width: calc(100% - 15vw);
  margin: auto;
  transition: margin-left 0.5s ease-in-out, width 0.5s ease-in-out;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;

  &::-webkit-scrollbar {
    display: none;
  }

  ${mobileAuthSideContainer}
`;

export const ContentWrapper = styled.div `
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: ${({ $embedded }) => ($embedded ? "none" : "1200px")};
  padding: ${({ $embedded }) => ($embedded ? "0.25rem 0 1rem" : "2rem")};
  display: flex;
  flex-direction: column;
  align-items: ${({ $embedded }) => ($embedded ? "stretch" : "center")};
  margin: 0 auto;
  box-sizing: border-box;
`;

export const ButtonContainerLeft = styled.div `
  display: flex;
  justify-content: flex-start;
  width: 100%;
  margin: 0.5%;
  gap: 0.5rem;
`;

// ── Botão ──────────────────────────────────────────────────────────────────────

export const StyledButton = styled.button `
  background: ${
	(props) => props.secondary ? "var(--input-bg)" : "var(--accent-secondary-soft)"
};
  border: 2px solid
    ${
	(props) => props.secondary ? "var(--border-subtle)" : "var(--border-default)"
};
  border-radius: 16px;
  color: ${
	(props) => props.secondary ? "var(--text-primary)" : "var(--text-heading)"
};
  cursor: pointer;
  padding: 12px 24px;
  text-align: center;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 14px;
  font-weight: 500;
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
  min-width: 140px;
  min-height: 44px;
  z-index: 2;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      ${
	(props) => props.secondary ? "var(--input-bg) 0%, var(--surface-glass) 100%" : "var(--accent-secondary-soft) 0%, var(--accent-soft) 100%"
}
    );
    opacity: 0;
    transition: opacity 0.4s ease;
    border-radius: 14px;
    z-index: -1;
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 14px;
    background: transparent;
    box-shadow: ${
	(props) => props.secondary ? "var(--shadow-sm)" : "0 6px 15px var(--accent-soft)"
};
    opacity: 0;
    transition: opacity 0.4s ease;
    z-index: -1;
  }

  &:hover {
    transform: translateY(-3px);
    border-color: ${
	(props) => props.secondary ? "var(--text-muted)" : "var(--accent)"
};
    color: var(--text-primary);

    &::before {
      opacity: 1;
    }
    &::after {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media screen and (max-width: 768px) {
    min-width: 120px;
    padding: 10px 20px;
  }
`;

// ── Cabeçalho ──────────────────────────────────────────────────────────────────

export const HeaderSection = styled.header `
  text-align: center;
  margin-bottom: 1rem;
  position: relative;
`;

export const Title = styled.h1 `
  font-size: 2.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, var(--accent-secondary) 0%, var(--accent) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0 0 2rem 0;
  text-shadow: 0 4px 20px color-mix(in srgb, var(--accent-secondary) 30%, transparent);
  text-align: center;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

// ── Tabela ─────────────────────────────────────────────────────────────────────

export const StyledTable = styled.table `
  width: 90%;
  border-collapse: collapse;
  background-color: var(--surface-1);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  font-size: 0.9rem;
  color: var(--text-primary);
  text-align: center;

  thead {
    background: var(--table-header-bg);
  }

  th {
    font-weight: var(--table-header-font-weight);
    color: var(--table-header-color);
    font-size: var(--table-header-font-size);
    text-transform: uppercase;
    letter-spacing: var(--table-header-letter-spacing);
  }

  th,
  td {
    padding: 12px 16px;
    border-bottom: 1px solid var(--border-subtle);
  }

  tbody tr:hover {
    background-color: var(--accent-soft);
    transition: background 0.2s ease-in-out;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  td[colspan="6"] {
    text-align: center;
    color: var(--text-muted);
    padding: 2rem;
  }

  @media (max-width: 768px) {
    font-size: 0.85rem;

    th,
    td {
      padding: 8px;
    }
  }
`;

// ── Paginação ──────────────────────────────────────────────────────────────────

export const PaginationContainer = styled.div `
  margin-top: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
`;

export const PageButton = styled.button `
  background: transparent;
  border: 2px solid var(--border-subtle);
  color: var(--text-primary);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  cursor: pointer;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  backdrop-filter: blur(8px);

  &:hover {
    border-color: var(--text-heading);
    color: var(--accent-secondary);
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
  }
`;

// ── Inline styles ──────────────────────────────────────────────────────────────

export const loadingWrapperStyle = {
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	justifyContent: "center",
	height: "100vh",
	gap: "1rem",
	color: "var(--text-heading)"
};

export const loadingSpinnerStyle = {
	width: 40,
	height: 40,
	border: "3px solid var(--border-subtle)",
	borderTop: "3px solid var(--text-heading)",
	borderRadius: "50%",
	animation: "spin 0.8s linear infinite"
};

export const loadingTextStyle = {
	fontSize: "0.95rem"
};

export const rowsBarStyle = {
	display: "flex",
	justifyContent: "flex-end",
	alignItems: "center",
	gap: "8px",
	padding: "8px 16px",
	width: "50%",
	marginLeft: "auto",
	marginBottom: "16px"
};

export const rowsLabelStyle = {
	color: "var(--text-heading)",
	fontSize: "0.85rem"
};

export const optionStyle = {


};

export const rowsSelectStyle = {
	padding: "6px 10px",
	borderRadius: "10px",
	fontSize: "0.85rem",
	background: "var(--table-row-hover)",
	color: "var(--text-on-accent)",
	border: "1px solid var(--border-default)",
	cursor: "pointer"
};

export const resultCountStyle = {
	width: "100%",
	textAlign: "right",
	marginBottom: "6px",
	color: "var(--text-muted)",
	fontSize: "0.8rem"
};

export const thIndexStyle = {
	width: 44
};

export const tdIndexStyle = {
	color: "var(--text-muted)",
	fontSize: "0.8rem",
	fontWeight: 500
};

export const emptyStateStyle = {
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	gap: "8px",
	padding: "2.5rem",
	color: "var(--text-muted)"
};

export const emptyStateIconStyle = {
	fontSize: "2rem"
};

export const ellipsisStyle = {
	color: "var(--text-muted)",
	padding: "0 4px"
};

export const pageButtonActiveStyle = {
	borderColor: "var(--text-heading)",
	color: "var(--accent-secondary)",
	background: "var(--accent-secondary-soft)"
};

// ── Específicos do AlertReportHistory ──────────────────────────────────────────

export const avatarStyle = {
	width: "32px",
	height: "32px",
	borderRadius: "50%",
	background: "color-mix(in srgb, var(--info) 20%, transparent)",
	color: "var(--info)",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	fontWeight: 700,
	fontSize: "0.75rem",
	flexShrink: 0
};

export const userCellStyle = {
	display: "flex",
	alignItems: "center",
	gap: "8px"
};

export const periodCellStyle = {
	fontFamily: "monospace",
	fontSize: "0.85rem",
	color: "var(--text-muted)"
};

// ── Modal ──────────────────────────────────────────────────────────────────────

export const Backdrop = styled.div `
  position: fixed;
  inset: 0;
  background: var(--surface-overlay);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
`;

export const Modal = styled.div `
  background: var(--app-bg-gradient);
  border: 1px solid var(--accent-secondary-soft);
  border-radius: 20px;
  width: 80%;
  height: 96%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: var(--shadow-lg);
`;

export const ModalHeader = styled.div `
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--accent-secondary-soft);
  gap: 1rem;
`;

export const ModalTitle = styled.h2 `
  font-size: 1.1rem;
  font-weight: 700;
  background: linear-gradient(135deg, var(--accent-secondary) 0%, var(--accent) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0 0 4px 0;
`;

export const ModalMeta = styled.p `
  font-size: 0.8rem;
  color: var(--text-muted);
  margin: 0;
`;

export const ModalActions = styled.div `
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`;

export const TableActionButton = styled.button `
  background: var(--accent-soft);
  border: 1.5px solid var(--border-default);
  border-radius: 10px;
  color: var(--info);
  cursor: pointer;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.25s ease;

  &:hover {
    background: var(--accent-soft);
    border-color: var(--info);
    color: var(--info);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const CloseButton = styled.button `
  background: var(--surface-glass);
  border: 2px solid var(--input-bg-focus);
  border-radius: 50%;
  color: var(--text-secondary);
  cursor: pointer;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: all 0.3s ease;

  &:hover {
    background: color-mix(in srgb, var(--danger) 15%, transparent);
    border-color: color-mix(in srgb, var(--danger) 40%, transparent);
    color: var(--danger);
    transform: rotate(90deg);
  }
`;

export const PdfViewer = styled.div `
  flex: 1;
  overflow: hidden;
  height: 10%;
  background: var(--surface-glass);
`;

// ── Filtro ─────────────────────────────────────────────────────────────────────

export const FilterButton = styled.button `
  padding: 8px 16px;
  border-radius: 16px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${
	(props) => props.secondary ? "var(--input-bg)" : "var(--accent-secondary-soft)"
};
  border: 2px solid
    ${
	(props) => props.secondary ? "var(--border-subtle)" : "var(--border-default)"
};
  color: ${
	(props) => props.secondary ? "var(--text-primary)" : "var(--text-heading)"
};

  &:hover {
    background-color: ${
	(props) => props.secondary ? "var(--border-subtle)" : "var(--border-subtle)"
};
    border-color: ${
	(props) => props.secondary ? "var(--text-muted)" : "var(--border-default)"
};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const filterBarStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: "0.75rem",
  marginBottom: "1rem",
  alignItems: "center",
  width: "100%",
  boxSizing: "border-box",
};

export const FilterLabel = styled.span`
  transition: color 0.2s ease;
  font-size: 1.5rem;
  font-weight: 400;
  color: var(--text-heading);
  margin: 0;
  letter-spacing: 0.05em;
  opacity: 0.9;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

export const FilterLabel2 = styled.span`
  transition: color 0.2s ease;
  font-size: 0.8rem;
  font-weight: 400;
  color: var(--text-heading);
  margin: 0;
  letter-spacing: 0.05em;
  opacity: 0.9;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

export const StyledSelect = styled.select`
  appearance: none;
  flex: 1 1 100%;
  width: 100%;
  min-height: 48px;
  padding: 0.75rem 2.75rem 0.75rem 1.15rem;
  border-radius: 16px;
  border: 1px solid var(--border-subtle);
  background-color: var(--input-bg);
  background-image:
    linear-gradient(45deg, transparent 50%, var(--text-heading) 50%),
    linear-gradient(135deg, var(--text-heading) 50%, transparent 50%);
  background-repeat: no-repeat;
  background-position:
    calc(100% - 18px) calc(50% - 2px),
    calc(100% - 13px) calc(50% - 2px);
  background-size: 5px 5px, 5px 5px;
  color: var(--text-primary);
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  outline: none;
  box-shadow: var(--shadow-sm);
  backdrop-filter: blur(20px);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  box-sizing: border-box;

  option {
    background-color: var(--surface-1);
    color: var(--text-primary);
  }

  &:hover,
  &:focus {
    border-color: var(--accent);
    box-shadow: var(--shadow-md);
  }
`;

export const StyledSelect2 = styled.select`
  appearance: none;
  padding: 0.55rem 2.2rem 0.55rem 0.9rem;
  border-radius: 12px;
  border: 1px solid var(--border-subtle);
  background-color: var(--input-bg);
  background-image:
    linear-gradient(45deg, transparent 50%, var(--text-heading) 50%),
    linear-gradient(135deg, var(--text-heading) 50%, transparent 50%);
  background-repeat: no-repeat;
  background-position:
    calc(100% - 14px) calc(50% - 2px),
    calc(100% - 9px) calc(50% - 2px);
  background-size: 5px 5px, 5px 5px;
  color: var(--text-primary);
  font-size: 0.88rem;
  font-weight: 500;
  cursor: pointer;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  min-width: 88px;
  width: auto;
  max-width: 200px;
  box-shadow: var(--shadow-sm);

  option {
    background-color: var(--surface-1);
    color: var(--text-primary);
  }

  &:hover,
  &:focus {
    border-color: var(--accent);
    box-shadow: var(--shadow-md);
  }
`;

export const StyledOption = styled.option`
  color: var(--text-primary);
  background-color: var(--surface-1);
  padding: 10px 12px;
  font-weight: 500;
  font-size: 14px;

  &:checked {
    background-color: var(--accent-soft);
    color: var(--text-heading);
    font-weight: 600;
  }
`;

// FilterButton estilizado
export const StyledFilterButton = styled.button `
  border: none;
  color: var(--text-color, #ef4444);
  background-color: rgba(239, 68, 68, 0.2);
  cursor: pointer;
  font-size: 13px;
  padding: 6px 12px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:hover {
    color: #ef4444;
    background-color: rgba(239, 68, 68, 0.1);
  }
  
  @media (prefers-color-scheme: dark) {
    --text-color: #9ca3af;
    
    &:hover {
      transform: translateY(-1px);
    }
  }
`;

export const DesktopOnly = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;

  @media screen and (max-width: 768px) {
    display: none;
  }
`;

export const MobileCards = styled.div`
  display: none;

  @media screen and (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    width: 100%;
  }
`;

export const MobileCard = styled.article`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: 0.9rem 1rem;
  border-radius: 14px;
  border: 1px solid var(--border-default);
  background: var(--surface-2);
  text-align: left;
  width: 100%;
  box-sizing: border-box;
`;

export const MobileCardTitle = styled.h3`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-heading);
`;

export const MobileCardMeta = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: var(--text-secondary);
  line-height: 1.4;
  word-break: break-word;
`;

export const MobileCardActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.35rem;

  button {
    width: 100%;
  }
`;
